import React, { createContext, useContext } from "react";
import { Box, Button, Flex, Grid, Heading, Input, Select, Spinner, Text } from "@chakra-ui/react";

export function PageHeader({ title, subtitle, children }) {
  return (
    <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap" mb={5}>
      <Box minW={0}>
        <Heading as="h1" fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1.15">
          {title}
        </Heading>
        {subtitle && <Text color="stone.500">{subtitle}</Text>}
      </Box>
      {children}
    </Flex>
  );
}

export function FilterTabs({ tabs, active, onChange }) {
  return (
    <Flex as="div" role="group" aria-label="Filter records" gap={1} overflowX="auto" mb={4} pb={1}>
      {tabs.map((tab) => {
        const isActive = active === tab.key;
        return (
          <Box
            as="button"
            type="button"
            key={tab.key}
            aria-pressed={isActive}
            onClick={() => onChange(tab.key)}
            flexShrink={0}
            px={3}
            py={1.5}
            borderRadius="md"
            border="1px solid"
            borderColor={isActive ? "turmeric.700" : "stone.500"}
            fontWeight={600}
            bg={isActive ? "turmeric.500" : "white"}
            color="ink"
            _hover={isActive ? undefined : { bg: "kumkum.50", borderColor: "kumkum.500" }}
          >
            {tab.label}{" "}
            <Text as="span" fontWeight={400}>
              {tab.count}
            </Text>
          </Box>
        );
      })}
    </Flex>
  );
}

export function SearchField({ options, onFieldChange, onSearch }) {
  return (
    <Flex gap={2} w={{ base: "100%", md: "auto" }}>
      <Select
        aria-label="Search by"
        w="150px"
        flexShrink={0}
        defaultValue={options[0].value}
        onChange={onFieldChange}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <Input
        type="search"
        aria-label="Search"
        placeholder="Search"
        minW={0}
        w={{ base: "100%", md: "240px" }}
        onChange={onSearch}
      />
    </Flex>
  );
}

// The "name" field matches first name, last name, or both together ("ravi kumar").
export function matchesSearch(record, field, search) {
  const value = field === "name" ? [record.fname, record.lname].filter(Boolean).join(" ") : record[field];
  return Boolean(value) && String(value).toLowerCase().includes(search.toLowerCase());
}

// Shows the first and last pages plus a window around the current one.
export function Pagination({ currentPage, pageCount, onPrevious, onNext, onPage }) {
  if (pageCount <= 1) {
    return null;
  }
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1).filter(
    (page) => page === 1 || page === pageCount || Math.abs(page - currentPage) <= 1
  );
  return (
    <Flex as="nav" aria-label="Pages" justify="flex-end" gap={1} mt={4} wrap="wrap">
      <Button variant="ghost" size="sm" onClick={onPrevious} isDisabled={currentPage === 1}>
        Previous
      </Button>
      {pages.map((page, index) => (
        <React.Fragment key={page}>
          {index > 0 && page - pages[index - 1] > 1 && (
            <Text px={1} alignSelf="center" color="stone.500">
              ...
            </Text>
          )}
          <Button
            size="sm"
            variant={currentPage === page ? "solid" : "ghost"}
            aria-current={currentPage === page ? "page" : undefined}
            onClick={() => onPage(page)}
          >
            {page}
          </Button>
        </React.Fragment>
      ))}
      <Button variant="ghost" size="sm" onClick={onNext} isDisabled={currentPage === pageCount}>
        Next
      </Button>
    </Flex>
  );
}

const LedgerContext = createContext("1fr");

export function Ledger({ columns, heads, isEmpty, emptyText, children }) {
  return (
    <LedgerContext.Provider value={columns}>
      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="stone.200" overflow="hidden">
        {isEmpty ? (
          <EmptyState>{emptyText}</EmptyState>
        ) : (
          <>
            <Grid
              display={{ base: "none", xl: "grid" }}
              templateColumns={columns}
              gap={4}
              px={5}
              py={2.5}
              fontWeight={600}
              color="kumkum.700"
              bg="kumkum.50"
              borderBottom="2px solid"
              borderColor="kumkum.500"
            >
              {heads.map((head, index) => (
                <Box key={head || index}>{head}</Box>
              ))}
            </Grid>
            {children}
          </>
        )}
      </Box>
    </LedgerContext.Provider>
  );
}

// stripe marks payment in the row margin: "paid", "unpaid" or nothing.
export function LedgerRow({ stripe, children }) {
  const columns = useContext(LedgerContext);
  const stripeColor = { paid: "tulsi.500", unpaid: "turmeric.500" }[stripe] || "transparent";
  return (
    <Grid
      templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)", xl: columns }}
      gap={{ base: 2, xl: 4 }}
      alignItems={{ base: "start", xl: "center" }}
      px={5}
      py={3}
      borderLeft="4px solid"
      borderLeftColor={stripeColor}
      borderBottom="1px solid"
      borderBottomColor="stone.200"
      _last={{ borderBottom: "none" }}
    >
      {children}
    </Grid>
  );
}

export function Cell({ label, children, full }) {
  return (
    <Box minW={0} gridColumn={{ base: full ? "1 / -1" : "auto", xl: "auto" }} overflowWrap="anywhere">
      {label && (
        <Text display={{ base: "block", xl: "none" }} fontSize="sm" color="stone.500">
          {label}
        </Text>
      )}
      {children}
    </Box>
  );
}

export function RowActions({ children }) {
  return (
    <Flex gap={1} wrap="wrap" justify={{ base: "flex-start", xl: "flex-end" }}>
      {children}
    </Flex>
  );
}

const STATUS_TONES = {
  Completed: "done",
  Paid: "done",
  Delivered: "done",
  Settled: "done",
  Pending: "open",
  Unpaid: "open",
  Undelivered: "open",
  Unsettled: "open",
};

export function StatusMark({ status }) {
  const tone = STATUS_TONES[status];
  return (
    <Box
      as="span"
      display="inline-block"
      px={2}
      borderRadius="sm"
      fontSize="sm"
      fontWeight={600}
      bg={tone === "done" ? "tulsi.100" : tone === "open" ? "turmeric.100" : "stone.100"}
      color={tone === "done" ? "tulsi.700" : tone === "open" ? "turmeric.700" : "stone.700"}
    >
      {status}
    </Box>
  );
}

export function EmptyState({ children }) {
  return (
    <Box px={5} py={10} textAlign="center" color="stone.500">
      {children}
    </Box>
  );
}

export function LoadingState({ children = "Loading" }) {
  return (
    <Flex direction="column" align="center" gap={3} py={16} color="stone.500" role="status">
      <Spinner color="kumkum.500" />
      {children}
    </Flex>
  );
}

// Plain section heading for use inside a page, below the Yatra One page title.
export function SectionTitle({ children }) {
  return (
    <Heading as="h2" fontSize="2xl" lineHeight="1.2" mt={8} mb={3}>
      {children}
    </Heading>
  );
}

export function Panel({ children, ...rest }) {
  return (
    <Box bg="white" borderRadius="lg" border="1px solid" borderColor="stone.200" {...rest}>
      {children}
    </Box>
  );
}
